import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAccountDeletionRequests,
  approveAccountDeletionRequest,
  rejectAccountDeletionRequest,
} from "../services/api";
import { getUserRole, getUserPermissions } from "../services/authService";

const PER_PAGE = 10;

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved (account deleted)",
  rejected: "Rejected",
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "N/A");

const AccountDeletionRequests = () => {
  const isAdmin = getUserRole() === "admin";
  const permissions = getUserPermissions()?.account_deletion_requests;
  const canApprove = isAdmin || permissions?.delete;
  const canReject = isAdmin || permissions?.edit;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchAccountDeletionRequests({ page, per_page: PER_PAGE, status, phone });
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message);
      setData([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  }, [page, status, phone]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setPhone(phoneInput.trim());
  };

  const handleReset = () => {
    setPhoneInput("");
    setPhone("");
    setStatus("");
    setPage(1);
  };

  const process = async (request, action, adminNote) => {
    setBusyId(request.id);
    setError("");
    try {
      await action(request.id, adminNote);
      // Step back a page when the last row of a later page just left this filter.
      if (data.length === 1 && page > 1) setPage(page - 1);
      else await loadRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = (request) => {
    if (!window.confirm(`Approve deletion of ${request.real_number}? This permanently deletes the user's account.`)) return;
    process(request, approveAccountDeletionRequest, "");
  };

  const openRejectModal = (request) => {
    setRejectNote("");
    setRejectTarget(request);
  };

  const closeRejectModal = () => {
    if (busyId !== null) return; // don't close while the request is in flight
    setRejectTarget(null);
  };

  const confirmReject = async () => {
    const request = rejectTarget;
    await process(request, rejectAccountDeletionRequest, rejectNote.trim());
    setRejectTarget(null);
  };

  const showActions = canApprove || canReject;
  const columns = showActions ? 9 : 8;

  return (
    <div className="page-container">
      <h2 className="page-title">Account Deletion Requests</h2>

      <form className="filter-form" onSubmit={handleSearch}>
        <select
          className="filter-input"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <input
          className="filter-input"
          type="text"
          placeholder="Phone number"
          value={phoneInput}
          onChange={(e) => setPhoneInput(e.target.value)}
        />
        <button type="submit" className="rate-btn">Search</button>
        <button type="button" className="rate-btn rate-btn-secondary" onClick={handleReset}>Reset</button>
      </form>

      {error && <div style={{ color: "#dc2626", marginBottom: 12 }}>{error}</div>}

      <div className="table-container">
        <table className="call-logs-table">
          <thead>
            <tr className="table-header-row">
              <th className="table-header">#</th>
              <th className="table-header">PHONE NUMBER</th>
              <th className="table-header">DID</th>
              <th className="table-header">REASON</th>
              <th className="table-header">REQUESTED AT</th>
              <th className="table-header">STATUS</th>
              <th className="table-header">PROCESSED AT</th>
              <th className="table-header">NOTE</th>
              {showActions && <th className="table-header">ACTIONS</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns} className="loading-cell">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={columns} className="empty-cell">No account deletion requests found</td></tr>
            ) : (
              data.map((request) => (
                <tr key={request.id} className="table-row">
                  <td className="table-cell">{request.id}</td>
                  <td className="table-cell">{request.real_number}</td>
                  <td className="table-cell">{request.did || "N/A"}</td>
                  <td className="table-cell">{request.reason || "N/A"}</td>
                  <td className="table-cell">{formatDateTime(request.created_at)}</td>
                  <td className="table-cell">
                    <span className={`deletion-status deletion-status-${request.status}`}>
                      {STATUS_LABELS[request.status] || request.status}
                    </span>
                  </td>
                  <td className="table-cell">{formatDateTime(request.processed_at)}</td>
                  <td className="table-cell">{request.admin_note || "N/A"}</td>
                  {showActions && (
                    <td className="table-cell">
                      {request.status === "pending" ? (
                        <div className="deletion-actions">
                          {canApprove && (
                            <button
                              type="button"
                              className="rate-btn rate-btn-danger"
                              disabled={busyId === request.id}
                              onClick={() => handleApprove(request)}
                            >
                              {busyId === request.id ? "Working..." : "Approve"}
                            </button>
                          )}
                          {canReject && (
                            <button
                              type="button"
                              className="rate-btn rate-btn-secondary"
                              disabled={busyId === request.id}
                              onClick={() => openRejectModal(request)}
                            >
                              Reject
                            </button>
                          )}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_items > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.current_page - 1) * PER_PAGE) + 1}–{Math.min(pagination.current_page * PER_PAGE, pagination.total_items)} of {pagination.total_items}{" "}
            requests (Page {pagination.current_page} of {pagination.total_pages})
          </div>
          <div className="pagination-buttons">
            <button
              onClick={() => setPage(pagination.current_page - 1)}
              disabled={!pagination.has_prev}
              className={`pagination-btn ${!pagination.has_prev ? "disabled" : ""}`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage(pagination.current_page + 1)}
              disabled={!pagination.has_next}
              className={`pagination-btn ${!pagination.has_next ? "disabled" : ""}`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="deletion-modal-backdrop" onClick={closeRejectModal}>
          <div
            className="deletion-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="reject-modal-title">Reject deletion request</h3>
            <p className="deletion-modal-text">
              The account of <strong>{rejectTarget.real_number}</strong> will be kept. You can leave a note explaining why.
            </p>
            <label className="deletion-modal-label" htmlFor="reject-note">Note (optional)</label>
            <textarea
              id="reject-note"
              className="deletion-modal-textarea"
              rows={4}
              maxLength={500}
              autoFocus
              placeholder="e.g. User has an unpaid balance"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <div className="deletion-modal-counter">{rejectNote.length}/500</div>
            <div className="deletion-modal-actions">
              <button
                type="button"
                className="rate-btn rate-btn-secondary"
                disabled={busyId !== null}
                onClick={closeRejectModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rate-btn rate-btn-danger"
                disabled={busyId !== null}
                onClick={confirmReject}
              >
                {busyId !== null ? "Rejecting..." : "Reject request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletionRequests;
